import { getPublicPosthogKey } from '@lib/posthog/utils/get-public-posthog-key';
import posthog from 'posthog-js';

import { recordFeatureFlagsLoadContext } from '../feature-flags/providers/feature-flag-load-state';
import { isPosthogEnabled } from '../utils/is-posthog-enabled';

/**
 * bootstrapClientObservability — initialises PostHog and any other client-only
 * providers that belong here. Sentry browser SDK init runs synchronously in
 * `src/instrumentation-client.ts` (required for correct ordering with
 * `global-error.tsx` and `onRouterTransitionStart`).
 *
 * Called once from src/instrumentation-client.ts outside the React lifecycle
 * to avoid React Strict Mode double-execution issues. Using a top-level import
 * (not dynamic import) ensures posthog is initialised synchronously before
 * the React tree renders and the AnalyticsProvider wraps the app.
 *
 * To add a new client-side provider that requires one-time setup:
 *   1. Implement the initialisation logic in the relevant provider file
 *   2. Import and call it here (unless it must run in instrumentation-client.ts)
 */
export function bootstrapClientObservability(): void {
  if (typeof window === 'undefined') {
    return;
  }

  bootstrapPostHog();
}

// TODO: move this to the posthog provider
function bootstrapPostHog(): void {
  if (!isPosthogEnabled()) return;

  const apiKey = getPublicPosthogKey();

  if (!apiKey) {
    // biome-ignore lint/suspicious/noConsole: eslint baseline parity
    console.error('[PostHog] NEXT_PUBLIC_POSTHOG_KEY is not set');
    return;
  }

  // PostHog powers feature flags + product analytics (pageviews,
  // autocapture, and explicit `posthog.capture()` events). Session
  // recording stays off — it accounted for ~18% of all request time in
  // the 2026-05 network audit and the app does not consume recordings.
  // Surveys and exception autocapture are also off: the app has no
  // surveys, and Sentry is the source of truth for exceptions.
  // Pin web vitals / performance capture so PostHog remote config cannot
  // silently disable it; session recording stays off (see above).
  // Docs: https://posthog.com/docs/libraries/js#config
  posthog.init(apiKey, {
    api_host: '/ingest',
    ui_host: 'https://us.posthog.com',
    capture_pageview: 'history_change',
    capture_pageleave: true,
    capture_performance: true,
    disable_session_recording: true,
    disable_surveys: true,
    capture_exceptions: false,
  });

  posthog.onFeatureFlags((_flags, _variants, context) => {
    recordFeatureFlagsLoadContext(context);
  });
}
